import { Fragment, useMemo } from "react";
import SummaryProps from "./SummaryProps";
import Loader from "../../UI/Loader";
import ApiErrorMessages from "../../UI/ApiErrorMessages";
import './Summary.css';
import FormSummaryField from "../../../models/serviceModels/FormSummaryField";
import { GroupedField, SectionGroupedFields } from "./SectionGroupedFields";
import { useReportSummaryQuery } from "../../../hooks/reports/queries/useReportQueries";
import { participantWorkedWithYearFilterEnabled } from "../../../utils/features";

const FormsSummary = (props: SummaryProps) => {
    const {
        data: summaries = [],
        isLoading: loading,
        error: apiError
    } = useReportSummaryQuery<FormSummaryField[]>(props.reportId);

    const groupedFields: SectionGroupedFields<FormSummaryField>[] = useMemo(() => {
        const grouping: SectionGroupedFields<FormSummaryField>[] = [];

        for (const field of summaries) {
            if (field.fieldName === 'participant_worked_with_year' && !participantWorkedWithYearFilterEnabled) {
                continue;
            }

            let existingSection = grouping.find(g => g.name === field.sectionName);
            if (!existingSection) {
                existingSection = {
                    name: field.sectionName,
                    fields: []
                };
                grouping.push(existingSection);
            }

            let existingGroupedField = existingSection.fields.find(f => f.name === field.fieldName);
            if (!existingGroupedField) {
                existingGroupedField = {
                    name: field.fieldName,
                    label: field.fieldLabel,
                    fields: [field]
                };
                existingSection.fields.push(existingGroupedField);
            } else {
                existingGroupedField.fields.push(field);
            }
        }

        return grouping;
    }, [summaries]);

    if (loading) {
        return <Loader />
    }

    const getSummaryDisplay = (grouping: GroupedField<FormSummaryField>) => {
        if (grouping.fields.length === 0) {
            return null;
        }

        const firstField = grouping.fields[0];

        if (firstField.averageValue === null && firstField.percentageValue === null && firstField.medianValue === null &&
            grouping.fields.length === 1 && firstField.totalValue !== null) {
            return (
                <div className="column-medium-3">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                        <div className="summary-overall-value">
                            {firstField.totalValue}
                        </div>
                    </div>
                </div>
            )
        }
        
        if (firstField.averageValue === null && firstField.percentageValue === null && firstField.medianValue === null &&
            firstField.totalValue === null) {
            return (
                <div className="column-medium-3">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                    </div>
                </div>
            )
        }

        if (firstField.percentageValue === null) {
            return (
                <div className="column-medium-6">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>total</th>
                                    <th>median</th>
                                    <th>average</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouping.fields.map((f, i) => {
                                    return <tr>
                                        <td>{f.totalValue}</td>
                                        <td>{f.medianValue && f.medianValue.toFixed(2)}</td>
                                        <td>{f.averageValue && f.averageValue.toFixed(2)}</td>
                                    </tr>
                                })}
                                {firstField.totalValue !== null && <tr className="border-top">
                                    <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                    <td></td>
                                    <td></td>
                                </tr>}
                            </tbody>
                        </table>
                    </div>
                </div>);
        }

        if (firstField.medianValue === null && firstField.percentageValue !== null) {
            return (
                <div className="column-medium-6">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>value</th>
                                    <th>total</th>
                                    <th>percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouping.fields.map((f, i) => {
                                    return <tr>
                                        <td><div className="summary-label-cell">{f.fieldGrouping}</div></td>
                                        <td>{f.totalValue && f.totalValue}</td>
                                        <td>{f.percentageValue && f.percentageValue.toFixed(2)}%</td>
                                    </tr>
                                })}
                                {firstField.totalValue !== null && <tr className="border-top">
                                    <td></td>
                                    <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                    <td></td>
                                </tr>}
                            </tbody>
                        </table>
                    </div>
                </div>);
        }

        return (
            <div className="column-medium-6">
                <div className="summary">
                    <h4>{grouping.label}</h4><table className="table">
                        <thead>
                            <tr>
                                <th>value</th>
                                <th>total</th>
                                <th>percentage</th>
                                <th>median</th>
                                <th>average</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grouping.fields.map((f, i) => {
                                return <tr className="padding-2">
                                    <td><div className="fw-100 summary-label-cell">{f.fieldGrouping}</div></td>
                                    <td>{f.totalValue && f.totalValue}</td>
                                    <td>{f.percentageValue && f.percentageValue.toFixed(2)}%</td>
                                    <td>{f.medianValue && f.medianValue.toFixed(2)}</td>
                                    <td>{f.averageValue && f.averageValue.toFixed(2)}</td>
                                </tr>
                            })}
                            {firstField.totalValue !== null && <tr className="border-top">
                                <td></td>
                                <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return <div className="summary-container">
        <h3>Summary for Submitted Tracking Forms</h3>
        <ApiErrorMessages error={apiError} />
        {groupedFields.map(grouping => {
            return <Fragment key={grouping.name}>
                <div className="row">
                    <div className="column">
                        <h4>{grouping.name}</h4>
                    </div>
                </div>
                <div className="row">
                    {grouping.fields.map(f => {
                        return <Fragment key={f.name}>
                            {getSummaryDisplay(f)}
                        </Fragment>
                    })}
                </div>
            </Fragment>
        })}
    </div>
}

export default FormsSummary;
